import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { useAuthStore } from '../../store/authStore';
import { parseToken } from '../../utils/authUtils';
import { getCentralAppUrl } from '../../config/authDomain';

const TOKEN_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function looksLikeJwt(token: string): boolean {
  if (!TOKEN_SHAPE.test(token.trim())) return false;
  return parseToken(token.trim()) !== null;
}

export const DevAuthHelperRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const centralAppUrl = getCentralAppUrl();

  const [tokenInput, setTokenInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const returnTo = searchParams.get('returnTo') ?? '/';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = tokenInput.trim();
    if (!trimmed) {
      setError('Paste an access token.');
      return;
    }
    if (!looksLikeJwt(trimmed)) {
      setError('That doesn\'t look like a JWT (expected three base64-url segments separated by dots).');
      return;
    }
    setSubmitting(true);
    try {
      await login({ accessToken: trimmed });
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply the token.');
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 560, width: '100%', borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon color="primary" />
            <Typography variant="h5" fontWeight={600}>
              Dev Auth Helper
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Paste an access token (JWT) from the production toolbox dashboard.
            The token is stored in this browser and used to authenticate every
            <code> /api/v3/*</code> call until it expires or is revoked.
          </Typography>

          {centralAppUrl && (
            <Alert severity="info" variant="outlined">
              SSO is configured at <code>{centralAppUrl}</code>. You'll usually
              be redirected there automatically — this form is a fallback for
              when that flow can't run (iframe blocked, localhost, etc.).
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Access token"
                placeholder="eyJhbGci…"
                multiline
                minRows={3}
                maxRows={10}
                value={tokenInput}
                onChange={e => setTokenInput(e.target.value)}
                error={!!error}
                helperText={error ?? ' '}
                autoFocus
                fullWidth
                inputProps={{ spellCheck: false, 'aria-label': 'access token' }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                fullWidth
              >
                {submitting ? 'Applying…' : 'Apply Token & Continue'}
              </Button>
            </Stack>
          </form>

          <Typography variant="caption" color="text.secondary">
            Tip: copy the token from DevTools → Application → Cookies on the
            production app (key <code>accessToken</code>).
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};
